import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import InfiniteScroll from "react-infinite-scroll-component";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import {
  GET_TABLES_GUESTS_API,
  GET_UNSEATED_GUESTS_API,
  MOVE_GUEST_API,
  GET_SEATING_HEADER_API,
  TAG_OPTIONS_API,
  GET_FLOORPLAN_API,
} from "../../Constant/api";
import { LoadingOverlay } from "../../Components/LoadingOverlay";
import Button from "../../Components/Button";
import { useToast } from "../../Components/Toast";
import { useNavigate } from "react-router-dom";
import FilterTextInput from "../../Components/FilterTextInput";
import DraggableGuestCard from "../../Components/SeatingChart/DraggableGuestCard";
import NormalMode from "./NormalMode";
import { Filter, Trash, Map1 } from "iconsax-react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import AddGuestModal from "../../Components/AddGuestModal";
import DraggableFloorplan from "../../Components/DraggableFloorplan";
import LastSavedIndicator from "../../Components/LastSavedIndicator";

const SeatingChart = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [unseated, setUnseated] = useState([]);
  const [tables, setTables] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const [headerData, setHeaderData] = useState([]);
  const [pendingMoves, setPendingMoves] = useState([]);
  const [tableStatuses, setTableStatuses] = useState({}); // { [tableId]: 'modified' | 'saving' | 'saved' }
  const tableTimersRef = useRef({});
  const [dropDownOpen, setDropdownOpen] = useState(false);
  const [tagList, setTagList] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [floorPlanImages, setFloorPlanImages] = useState([]);
  const [showFloorplanModal, setShowFloorplanModal] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isAutosaveEnabled, setIsAutosaveEnabled] = useState(true);
  const [nextAutosaveTime, setNextAutosaveTime] = useState(null);

  const { toast } = useToast();
  const navigate = useNavigate();

  const saveTableChanges = useCallback(async (tableId) => {
    setTableStatuses((prev) => ({ ...prev, [tableId]: "saving" }));

    // Get all moves that involve this table as source or destination
    // We need to access the LATEST pendingMoves here.
    // Since this is in a timeout, we should use a functional update or a ref to pendingMoves.
    setPendingMoves((currentMoves) => {
      const movesToSave = currentMoves.filter(
        (m) => m.tableId === tableId || m.sourceTableId === tableId,
      );

      if (movesToSave.length === 0) {
        setTableStatuses((prev) => ({ ...prev, [tableId]: "saved" }));
        setTimeout(() => {
          setTableStatuses((prev) => ({ ...prev, [tableId]: null }));
        }, 3000);
        return currentMoves;
      }

      // Perform the API call
      axios
        .post(
          MOVE_GUEST_API,
          { moves: movesToSave },
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
            },
          },
        )
        .then(() => {
          setTableStatuses((prev) => ({ ...prev, [tableId]: "saved" }));
          setLastSaved(new Date());
          // Clear "saved" status after 3 seconds
          setTimeout(() => {
            setTableStatuses((prev) => ({ ...prev, [tableId]: null }));
          }, 3000);
        })
        .catch((error) => {
          console.error(`Failed to autosave table ${tableId}:`, error);
          setTableStatuses((prev) => ({ ...prev, [tableId]: "modified" }));
          toast(`Autosave failed for table ${tableId}`, "error");
        });

      // Return moves that were NOT saved
      return currentMoves.filter(
        (m) => !(m.tableId === tableId || m.sourceTableId === tableId),
      );
    });
  }, [toast]);

  const markTableModified = useCallback(
    (tableId) => {
      if (!tableId || !isAutosaveEnabled) return;

      setTableStatuses((prev) => ({ ...prev, [tableId]: "modified" }));

      if (tableTimersRef.current[tableId]) {
        clearTimeout(tableTimersRef.current[tableId]);
      }

      tableTimersRef.current[tableId] = setTimeout(() => {
        saveTableChanges(tableId);
      }, 5000);
    },
    [isAutosaveEnabled, saveTableChanges],
  );

  const autoAssignPositions = useCallback(
    (tablesData) => {
      if (!isAutosaveEnabled) return;

      const newMoves = [];
      const modifiedTableIds = new Set();

      tablesData.forEach((table) => {
        const pax = table.pax;
        const guests = table.guest_list || [];

        // Find occupied positions
        const occupiedPositions = new Set(
          guests
            .filter(
              (g) =>
                g.position !== null &&
                g.position !== undefined &&
                g.position < pax,
            )
            .map((g) => g.position),
        );

        // Find unpositioned guests (null position or position >= pax)
        const unpositionedGuests = guests.filter(
          (g) =>
            g.position === null ||
            g.position === undefined ||
            g.position >= pax,
        );

        if (unpositionedGuests.length === 0) return;

        // Find available positions
        let nextAvailablePos = 0;
        unpositionedGuests.forEach((guest) => {
          while (
            nextAvailablePos < pax &&
            occupiedPositions.has(nextAvailablePos)
          ) {
            nextAvailablePos++;
          }

          if (nextAvailablePos < pax) {
            // Assign this position
            newMoves.push({
              guestId: guest.guest_id,
              tableId: table.table_id,
              position: nextAvailablePos,
              sourceTableId: table.table_id,
            });
            occupiedPositions.add(nextAvailablePos);
            modifiedTableIds.add(table.table_id);

            // Update the guest object in state as well so UI reflects it immediately
            guest.position = nextAvailablePos;
            nextAvailablePos++;
          }
        });
      });

      if (newMoves.length > 0) {
        setPendingMoves((prev) => {
          const updated = [...prev];
          newMoves.forEach((move) => {
            const idx = updated.findIndex((m) => m.guestId === move.guestId);
            if (idx !== -1) {
              updated[idx] = move;
            } else {
              updated.push(move);
            }
          });
          return updated;
        });

        modifiedTableIds.forEach((id) => markTableModified(id));
      }
    },
    [isAutosaveEnabled, markTableModified],
  );

  const fetchFloorplanImage = useCallback(async () => {
    return axios
      .get(GET_FLOORPLAN_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { eventId: sessionStorage.getItem("event_id") },
      })
      .then((response) => {
        const data = response.data.data;
        if (data && Array.isArray(data.floor_plan)) {
          setFloorPlanImages(data.floor_plan);
        } else if (data && typeof data.floor_plan === "string") {
          setFloorPlanImages([data.floor_plan]);
        } else {
          setFloorPlanImages([]);
        }
      })
      .catch((error) => {
        toast("Failed to fetch floorplan image.", "error");
        console.error(error);
      });
  }, [toast]);

  // Fetch Header Data
  const fetchHeaderData = useCallback(async () => {
    try {
      return axios
        .get(GET_SEATING_HEADER_API, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: { eventId: sessionStorage.getItem("event_id") },
        })
        .then((response) => {
          setHeaderData(response.data.data);
        })
        .catch((error) => {
          toast("Failed to fetch header data.", "error");
          console.error(error);
        });
    } catch (error) {
      toast("Failed to fetch header data.", "error");
      console.error(error);
    }
  }, [toast]);

  const fetchTagList = useCallback((tags) => {
    return axios
      .get(TAG_OPTIONS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { eventId: sessionStorage.getItem("event_id") },
      })
      .then((response) => {
        setTagList(response.data.data);
      })
      .catch((error) => {
        console.error(error);
        toast("Failed to fetch tag list", "error");
      });
  }, [toast]);

  // Helper function to get all seated guest IDs from tables data
  const getSeatedGuestIdsFromTables = (tablesData) => {
    const seatedIds = new Set();
    tablesData.forEach((table) => {
      if (table.guest_list) {
        table.guest_list.forEach((guest) => {
          seatedIds.add(guest.guest_id);
        });
      }
    });
    return seatedIds;
  };

  // Fetch unseated
  const fetchUnseated = useCallback(async (
    pageNum = 1,
    reset = false,
    tablesData = null,
  ) => {
    // Use provided tablesData or the latest state via a functional check if needed, 
    // but we'll pass it explicitly to avoid dependency loops.
    const seatedGuestIds = getSeatedGuestIdsFromTables(tablesData || []);

    return axios
      .get(GET_UNSEATED_GUESTS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          eventId: sessionStorage.getItem("event_id"),
          perPage: 20,
          page: pageNum,
          search,
          excludeGuestIds: Array.from(seatedGuestIds).join(","),
          tags: selectedTags,
        },
      })
      .then((res) => {
        const newData = res.data.data;

        setUnseated((prev) =>
          reset ? newData[0].guest_list : [...prev, ...newData[0].guest_list],
        );
        setHasMore(pageNum < newData[0].pagination.last_page);
      })
      .catch((error) => {
        toast("Failed to fetch unseated guests.", "error");
        console.error(error);
      });
  }, [search, selectedTags, toast]);

  // Fetch tables
  const fetchTables = useCallback(async () => {
    return axios
      .get(GET_TABLES_GUESTS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { eventId: sessionStorage.getItem("event_id") },
      })
      .then((response) => {
        const data = response.data.data;
        setTables(data);
        // Automatically assign positions to guests with null positions
        autoAssignPositions(data);
        return data;
      })
      .catch((error) => {
        toast("Failed to fetch table data.", "error");
        console.error(error);
        throw error; // Re-throw to handle in calling function
      });
  }, [toast, autoAssignPositions]);

  const handleUnassignIndividualGuest = (guestId) => {
    console.log(guestId);
    const table = tables.find((t) =>
      t.guest_list.some((g) => g.guest_id === guestId),
    );
    if (!table) {
      toast("Guest not found in any table.", "error");
      return;
    }

    const sourceTableId = table.table_id;

    // Move guest from the table to unseated
    const guestIndex = table.guest_list.findIndex(
      (g) => g.guest_id === guestId,
    );
    if (guestIndex > -1) {
      const [removedGuest] = table.guest_list.splice(guestIndex, 1);
      setUnseated((prev) => [...prev, removedGuest]);
    }
    const updatedTables = tables.map((t) => {
      if (t.table_id === table.table_id) {
        return { ...t, guest_list: table.guest_list };
      }
      return t;
    });
    setTables(updatedTables);
    // Track move for the guest
    setPendingMoves((prev) => [
      ...prev,
      { guestId: guestId, tableId: null, position: null, sourceTableId },
    ]);

    markTableModified(sourceTableId);
  };

  const handleUnassignAllGuests = (tableId) => {
    const table = tables.find((t) => t.table_id === tableId);
    if (!table) return;

    // Move all guests from the table to unseated
    const updatedUnseated = [...unseated, ...table.guest_list];
    setUnseated(updatedUnseated);
    const updatedTables = tables.map((t) => {
      if (t.table_id === tableId) {
        return { ...t, guest_list: [] };
      }
      return t;
    });
    setTables(updatedTables);

    // Track moves for all guests
    const newMoves = table.guest_list.map((guest, index) => ({
      guestId: guest.guest_id,
      tableId: null,
      position: null,
      sourceTableId: tableId,
    }));
    setPendingMoves((prev) => [...prev, ...newMoves]);

    markTableModified(tableId);
  };

  const loadMore = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    setLoading(true);
    await fetchUnseated(nextPage, false, tables);
    setLoading(false);
  };

  const onDragEnd = (result) => {
    const { source, destination, combine, draggableId } = result;

    // Handle both normal drops and combine drops (swaps)
    if (!destination && !combine) return;

    const guestId = parseInt(draggableId);

    const parseId = (id, dropIndex) => {
      if (id === "-2") return { tableId: null, seatIndex: dropIndex };
      if (id.startsWith("table-")) {
        const parts = id.split("-");
        // table-{tableId}-seat-{seatIndex} or table-{tableId}-extra-{extraIndex}
        const type = parts[2];
        const tableId = parseInt(parts[1]);
        const seatIndex = parseInt(parts[3]);
        return { tableId, seatIndex: type === "extra" ? null : seatIndex };
      }
      return { tableId: parseInt(id), seatIndex: dropIndex };
    };

    const sourceInfo = parseId(source.droppableId, source.index);
    const sourceTableId = sourceInfo.tableId;

    // Determine destination info
    let destinationTableId;
    let destinationSeatIndex;

    if (combine) {
      const destInfo = parseId(combine.droppableId, 0);
      destinationTableId = destInfo.tableId;
      destinationSeatIndex = destInfo.seatIndex;
    } else {
      const destInfo = parseId(destination.droppableId, destination.index);
      destinationTableId = destInfo.tableId;
      destinationSeatIndex = destInfo.seatIndex;
    }

    // Adjust seat index for guest list push logic
    // If we're dropping into an "extra" slot, we don't have a seat index in the ID
    // but we can infer it if needed. For now, the push logic handles it.

    // Handle dropping onto a guest directly (swap) or dropping onto an empty seat
    // If destination is defined, it means we dropped onto a Droppable (the seat).
    // If combine is defined, it means we dropped onto a Draggable (the guest).

    // DEBUG: Log drop info to help troubleshoot
    console.log("onDragEnd Drop Info:", {
      guestId,
      source: { tableId: sourceTableId, seatIndex: sourceInfo.seatIndex, droppableId: source.droppableId },
      destination: { tableId: destinationTableId, seatIndex: destinationSeatIndex, droppableId: destination?.droppableId },
      combine: !!combine,
      combineDraggableId: combine?.draggableId
    });

    if (
      sourceTableId === destinationTableId &&
      sourceInfo.seatIndex === destinationSeatIndex &&
      !combine
    ) {
      console.log("onDragEnd: Early return because source and destination are same");
      return;
    }

    // Clone current data
    const updatedTables = JSON.parse(JSON.stringify(tables));
    const updatedUnseated = [...unseated];
    let movedGuest;

    console.log("onDragEnd: Processing move...");

    // Remove guest from source
    if (sourceTableId === null) {
      const guestIndex = updatedUnseated.findIndex(
        (g) => g.guest_id === guestId,
      );
      if (guestIndex > -1) {
        movedGuest = updatedUnseated[guestIndex];
        updatedUnseated.splice(guestIndex, 1);
      }
    } else {
      const tableIndex = updatedTables.findIndex(
        (t) => t.table_id === sourceTableId,
      );
      const guestIndex = updatedTables[tableIndex]?.guest_list.findIndex(
        (g) => g.guest_id === guestId,
      );
      if (guestIndex > -1) {
        movedGuest = updatedTables[tableIndex].guest_list[guestIndex];
        updatedTables[tableIndex].guest_list.splice(guestIndex, 1);
      }
    }

    if (!movedGuest) return;

    // Add to destination
    if (destinationTableId === null) {
      movedGuest.position = null;
      updatedUnseated.splice(destination.index, 0, movedGuest);
    } else {
      const destTableIndex = updatedTables.findIndex(
        (t) => t.table_id === destinationTableId,
      );
      if (destTableIndex !== -1) {
        const destTable = updatedTables[destTableIndex];

        // If we have a combine.draggableId, we dropped onto a guest.
        // If not, we dropped onto an empty seat.
        const combinedGuestId = combine ? parseInt(combine.draggableId) : null;
        
        const existingGuestIndex = combinedGuestId 
          ? destTable.guest_list.findIndex(g => g.guest_id === combinedGuestId)
          : destTable.guest_list.findIndex(g => g.position === destinationSeatIndex);

        if (existingGuestIndex !== -1) {
          const swappedGuest = destTable.guest_list[existingGuestIndex];
          // Use the actual position of the guest we're swapping with
          const targetSeatIndex = swappedGuest.position;

          if (sourceTableId === null) {
            // Case 1: Dragging from Unseated to occupied Seat
            // Swap: Move the existing guest to Unseated
            swappedGuest.position = null;
            updatedUnseated.push(swappedGuest);

            // Replace in destination table
            destTable.guest_list[existingGuestIndex] = {
              ...movedGuest,
              position: targetSeatIndex,
            };
          } else {
            // Case 2: Table-to-Table Swap
            const sourceTableIndex = updatedTables.findIndex(
              (t) => t.table_id === sourceTableId,
            );

            if (sourceTableIndex !== -1) {
              const sourceTable = updatedTables[sourceTableIndex];
              const sourceSeatIndex = sourceInfo.seatIndex;

              // Move existing guest to source seat
              swappedGuest.position = sourceSeatIndex;
              sourceTable.guest_list.push(swappedGuest);

              // Move dragged guest to destination seat
              destTable.guest_list[existingGuestIndex] = {
                ...movedGuest,
                position: targetSeatIndex,
              };

              // Track move for swapped guest
              setPendingMoves((prev) => {
                const existingMoveIdx = prev.findIndex(
                  (m) => m.guestId === swappedGuest.guest_id,
                );
                const newMove = {
                  guestId: swappedGuest.guest_id,
                  tableId: sourceTableId,
                  position: sourceSeatIndex,
                  sourceTableId: destinationTableId, // Originated from destination table in this swap
                };
                if (existingMoveIdx !== -1) {
                  const updated = [...prev];
                  updated[existingMoveIdx] = newMove;
                  return updated;
                }
                return [...prev, newMove];
              });
            }
          }
          // Update the destinationSeatIndex for tracking
          destinationSeatIndex = targetSeatIndex;
        } else {
          // Normal move to empty seat
          movedGuest.position = destinationSeatIndex;
          destTable.guest_list.push(movedGuest);
        }
      }
    }

    // Update UI
    setTables(updatedTables);
    setUnseated(updatedUnseated);

    // Track move for later saving
    setPendingMoves((prev) => {
      const position =
        destinationTableId !== null ? destinationSeatIndex : null;
      const existingIndex = prev.findIndex((m) => m.guestId === guestId);

      if (existingIndex !== -1) {
        const updatedMoves = [...prev];
        updatedMoves[existingIndex] = {
          ...updatedMoves[existingIndex],
          tableId: destinationTableId,
          position: position,
          sourceTableId: sourceTableId,
        };
        return updatedMoves;
      } else {
        return [
          ...prev,
          {
            guestId: guestId,
            tableId: destinationTableId,
            position: position,
            sourceTableId: sourceTableId,
          },
        ];
      }
    });

    // Mark involved tables as modified
    if (sourceTableId) markTableModified(sourceTableId);
    if (destinationTableId) markTableModified(destinationTableId);
  };

  const handleSaveChanges = async () => {
    if (pendingMoves.length === 0) {
      toast("No changes to save.", "info");
      return;
    }
    setLoading(true);
    setIsSaving(true);
    try {
      await axios.post(
        MOVE_GUEST_API,
        { moves: pendingMoves },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
        },
      );
      toast("Guest seating updated successfully.", "success");
      setPendingMoves([]);
      setTableStatuses({}); // Clear all statuses
      setLastSaved(new Date());
      await handleRefresh(false);
    } catch (error) {
      toast("Failed to save changes.", "error");
      console.error(error);
    } finally {
      setIsSaving(false);
      setLoading(false);
    }
  };

  const handleRefresh = useCallback(async (showOverlay = true) => {
    if (showOverlay) setLoading(true);
    try {
      const tablesData = await fetchTables();
      await fetchUnseated(1, true, tablesData);
    } catch (error) {
      await fetchUnseated(1, true, []);
    } finally {
      if (showOverlay) setLoading(false);
    }
  }, [fetchTables, fetchUnseated]);

  // Per-table autosave logic replaces the global timer
  const autosaveTimerRef = useRef(null);

  useEffect(() => {
    // Cleanup timers on unmount
    return () => {
      Object.values(tableTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  // Initial Load
  useEffect(() => {
    const initFetch = async () => {
      setInitialLoading(true);
      try {
        await Promise.all([
          fetchHeaderData(),
          fetchTagList(),
          fetchFloorplanImage(),
        ]);
        const tablesData = await fetchTables();
        await fetchUnseated(1, true, tablesData);
      } catch (error) {
        console.error("Initial fetch failed:", error);
      } finally {
        setInitialLoading(false);
      }
    };

    initFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleFloorplan = () => {
    if (!showFloorplanModal) {
      fetchFloorplanImage();
    }
    setShowFloorplanModal(!showFloorplanModal);
  };

  // Handle Tag Filtering
  useEffect(() => {
    const filterByTags = async () => {
      setPage(1);
      setLoading(true);
      await fetchUnseated(1, true, tables);
      setLoading(false);
    };
    filterByTags();
  }, [selectedTags, fetchUnseated]);

  return (
    <div className="flex flex-col w-full h-full min-h-screen">
      {(loading || initialLoading) && <LoadingOverlay />}

      {/* Header */}
      <div className="sticky grid grid-cols-3 w-full p-[24px] h-full max-h-[80px] border border-[#EFF0F2] bg-white items-center justify-between">
        <h1 className="flex items-center gap-4 text-[20px]">
          Seating Chart
          {pendingMoves.length > 0 && (
            <span className="text-[#9CA3AF] text-[14px] font-normal">
              Yet to Save.
            </span>
          )}
        </h1>
        <div className="flex justify-center items-center w-full">
          {headerData && headerData.length > 0 && (
            <div className="flex items-center justify-center gap-[24px]">
              {headerData.map((data, index) => (
                <div className="flex flex-col items-center justify-center">
                  <p className="font-normal text-[#636C7E] text-[12px]">
                    {data.label}
                  </p>
                  <h2 className="font-semibold text-14px">{data.value}</h2>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid w-full items-center justify-end">
          <div className="flex items-center gap-[12px]">
            <LastSavedIndicator
              lastSaved={lastSaved}
              isSaving={isSaving}
              nextAutosaveTime={nextAutosaveTime}
              hasPendingChanges={pendingMoves.length > 0}
            />
            <div
              className="flex flex-col items-center justify-center gap-1"
              title="Toggle Autosave"
            >
              <span className="text-gray-500 text-[12px] font-normal leading-tight">
                Autosave
              </span>
              <button
                className={`w-9 h-5 rounded-full p-0.5 transition-colors relative ${
                  isAutosaveEnabled ? "bg-green-500" : "bg-gray-300"
                }`}
                onClick={() => setIsAutosaveEnabled(!isAutosaveEnabled)}
              >
                <div
                  className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 absolute top-0.5 left-0.5 ${
                    isAutosaveEnabled ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            <div className="flex items-center gap-[8px]">
              <Button
                variant="icon"
                onClick={handleToggleFloorplan}
                title={
                  showFloorplanModal ? "Close Floorplan" : "View Floorplan"
                }
              >
                <Map1
                  size="20"
                  color={showFloorplanModal ? "#5C4BF5" : "#636C7E"}
                  variant={showFloorplanModal ? "Bold" : "Linear"}
                />
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  if (pendingMoves.length > 0) {
                    if (
                      window.confirm(
                        "You have unsaved changes. Are you sure you want to leave without saving?",
                      )
                    ) {
                      navigate(-1);
                    } else {
                      return;
                    }
                  } else {
                    navigate(-1);
                  }
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex h-full w-full">
          {/* Sidebar */}
          <div className="flex flex-col w-full h-[calc(100vh-80px)] max-w-[250px] xl:max-w-[400px] bg-white border-r border-[#EFF0F2] no-scrollbar overflow-y-auto">
            <div className="flex flex-col w-full p-[12px] xl:p-[24px] border-b border-[#EFF0F2] gap-[24px]">
              <div className="flex flex-col w-full text-[14px]">
                <h2>Guest List</h2>
                <p className="font-normal text-[#636C7E]">
                  You can assign seat by drag and drop, or search in the table
                  chart.
                </p>
              </div>
              <div className="relative flex items-center gap-[6px]">
                <FilterTextInput
                  placeholder="Search Guest"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  onKeyDown={async (e) => {
                    if (e.key === "Enter") {
                      setPage(1);
                      setLoading(true);
                      await fetchUnseated(1, true, tables);
                      setLoading(false);
                    }
                  }}
                  className="w-full"
                />
                <Filter
                  size="32"
                  color="#5C4BF5"
                  variant="Bold"
                  className="cursor-pointer"
                  onClick={() => {
                    setDropdownOpen(!dropDownOpen);
                  }}
                />
                {dropDownOpen && (
                  <div className="absolute right-0 top-12 p-1 bg-white shadow-lg z-20 rounded-lg font-normal">
                    {tagList && tagList.length > 0 ? (
                      <div className="flex flex-col gap-1">
                        {tagList.map((tag) => (
                          <div
                            className="flex gap-2 items-center p-2"
                            key={tag.id}
                          >
                            <input
                              type="checkbox"
                              checked={selectedTags.includes(tag.id)}
                              className="accent-[#5C4BF5] w-4 h-4"
                              onChange={() => {
                                if (selectedTags.includes(tag.id)) {
                                  setSelectedTags(
                                    selectedTags.filter((id) => id !== tag.id),
                                  );
                                } else {
                                  setSelectedTags([...selectedTags, tag.id]);
                                }
                              }}
                            />{" "}
                            {tag.name}
                            <span className="text-[11px] font-medium px-[6px] py-[2px] text-[#5C4BF5] bg-[#EFEDFE] border border-[#DEDBFD] rounded-md">
                              {tag.guests_count}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div>No tags available</div>
                    )}
                  </div>
                )}
              </div>
              {selectedTags.length > 0 && (
                <div className="flex flex-wrap w-full items-center gap-2">
                  {selectedTags.map((tagId) => (
                    <span
                      className="flex items-center gap-2 bg-[#EFF0F2] rounded-full px-3 py-[6px]"
                      key={tagId}
                    >
                      {tagList.find((tag) => tag.id === tagId)?.name}
                      <XMarkIcon
                        className="size-[12px] cursor-pointer"
                        onClick={() => {
                          setSelectedTags(
                            selectedTags.filter((id) => id !== tagId),
                          );
                          setPage(1);
                          fetchUnseated(1, true);
                        }}
                      />
                    </span>
                  ))}
                  <span
                    className="flex items-center gap-2 cursor-pointer text-[#FF3030]"
                    onClick={() => {
                      setSelectedTags([]);
                    }}
                  >
                    <Trash size="12" variant="Bold" />
                    Clear All
                  </span>
                </div>
              )}
            </div>

            <Droppable droppableId="-2">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="flex flex-col w-full p-[24px] border-t border-[#becbe4] gap-[16px] overflow-auto"
                  id="scrollableUnseated"
                >
                  <InfiniteScroll
                    dataLength={unseated.length}
                    next={loadMore}
                    hasMore={hasMore}
                    loader={
                      <p className="text-center text-gray-400">Loading...</p>
                    }
                    scrollableTarget="scrollableUnseated"
                  >
                    {unseated.map((guest, index) => (
                      <DraggableGuestCard
                        data={guest}
                        index={index}
                        tableId={-2}
                        key={guest.guest_id}
                        pendingMoves={pendingMoves}
                      />
                    ))}
                  </InfiniteScroll>
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>

          {/* Tables */}
          <div className="flex flex-col w-full h-[calc(100vh-80px)] p-[12px] xl:p-[24px] no-scrollbar overflow-y-auto">
            <NormalMode
              // setMode={setMode}
              pendingMoves={pendingMoves}
              tableData={tables}
              tableStatuses={tableStatuses}
              handleRefresh={handleRefresh}
              handleSaveChanges={handleSaveChanges}
              handleUnassignAllGuests={handleUnassignAllGuests}
              handleUnassignIndividualGuest={handleUnassignIndividualGuest}
              onAddGuest={() => setShowAddModal(true)}
              showFloorplan={showFloorplanModal}
              onToggleFloorplan={handleToggleFloorplan}
            />
          </div>
        </div>
      </DragDropContext>
      <AddGuestModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleRefresh}
      />
      {showFloorplanModal && (
        <DraggableFloorplan
          floorPlanImages={floorPlanImages}
          onClose={handleToggleFloorplan}
        />
      )}
    </div>
  );
};

export default SeatingChart;
